import { useEffect, useRef, type RefObject } from 'react';
import type mapboxgl from 'mapbox-gl';
import type { SelectMode } from './MapTypes';
import type { Pin } from './mapPins/MapPinTypes';

// A drag shorter than this is a stray click, not a rectangle.
const MIN_AREA_PX = 4;

type Point = { x: number; y: number };

const NOT_TYPED_IN = ['button', 'checkbox', 'radio', 'range', 'reset', 'submit'];

const typesText = (target: EventTarget | null) =>
	target instanceof HTMLElement &&
	(target.isContentEditable ||
		target instanceof HTMLTextAreaElement ||
		target instanceof HTMLSelectElement ||
		(target instanceof HTMLInputElement && !NOT_TYPED_IN.includes(target.type)));

// Esc belongs to whatever is focused or open on top of the map: a field, a modal.
const escapeIsTaken = (event: KeyboardEvent) =>
	event.defaultPrevented ||
	typesText(event.target) ||
	!!document.querySelector('.Modals__curtain--visible, body.modal-open');

const useSelectMode = <T>(
	mapRef: RefObject<mapboxgl.Map | null>,
	inited: boolean,
	selectMode: SelectMode<T> | undefined,
	pins: Pin<T>[],
	boxClassName: string
) => {
	const listener = useRef(selectMode);
	const currentPins = useRef(pins);
	useEffect(() => {
		listener.current = selectMode;
		currentPins.current = pins;
	}, [selectMode, pins]);
	const active = !!selectMode?.active;

	useEffect(() => {
		const map = mapRef.current;
		if (!inited || !map || !active) {
			return;
		}
		const container = map.getContainer();
		const canvas = map.getCanvasContainer();
		const point = (event: MouseEvent): Point => {
			const rect = container.getBoundingClientRect();
			return { x: event.clientX - rect.left, y: event.clientY - rect.top };
		};
		let start: Point | null = null;
		let box: HTMLDivElement | null = null;

		const move = (event: PointerEvent) => {
			if (!start) {
				return;
			}
			const to = point(event);
			if (!box) {
				// Captured only once it is a drag, so a Shift-click still lands on the pin it was on.
				canvas.setPointerCapture(event.pointerId);
				box = document.createElement('div');
				box.className = boxClassName;
				container.appendChild(box);
			}
			box.style.left = `${Math.min(start.x, to.x)}px`;
			box.style.top = `${Math.min(start.y, to.y)}px`;
			box.style.width = `${Math.abs(start.x - to.x)}px`;
			box.style.height = `${Math.abs(start.y - to.y)}px`;
		};
		const stopDrawing = () => {
			box?.remove();
			box = null;
			if (start) {
				start = null;
				map.dragPan.enable();
				map.dragRotate.enable();
			}
		};
		const up = (event: PointerEvent) => {
			const from = start;
			stopDrawing();
			if (!from) {
				return;
			}
			const to = point(event);
			if (Math.abs(from.x - to.x) < MIN_AREA_PX || Math.abs(from.y - to.y) < MIN_AREA_PX) {
				return;
			}
			// Tested on screen, not as a lat/lng box: once the map is rotated or pitched the two
			// corners no longer describe the rectangle that was drawn.
			const [left, right] = [Math.min(from.x, to.x), Math.max(from.x, to.x)];
			const [top, bottom] = [Math.min(from.y, to.y), Math.max(from.y, to.y)];
			const inside = currentPins.current.filter(pin => {
				const { x, y } = map.project([pin.longitude, pin.latitude]);
				return x >= left && x <= right && y >= top && y <= bottom;
			});
			listener.current?.onArea(inside);
		};
		const down = (event: PointerEvent) => {
			if (event.button !== 0 || !event.shiftKey) {
				return;
			}
			// Also keeps the mouse events from reaching Mapbox, whose own Shift-drag would zoom.
			event.preventDefault();
			map.dragPan.disable();
			map.dragRotate.disable();
			start = point(event);
		};
		const keydown = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && !escapeIsTaken(event)) {
				listener.current?.onExit();
			} else if (event.key === 'Shift') {
				canvas.style.cursor = 'crosshair';
			}
		};
		const keyup = (event: KeyboardEvent) => {
			if (event.key === 'Shift') {
				canvas.style.cursor = '';
			}
		};

		map.boxZoom.disable();
		canvas.addEventListener('pointerdown', down);
		canvas.addEventListener('pointermove', move);
		canvas.addEventListener('pointerup', up);
		canvas.addEventListener('pointercancel', up);
		document.addEventListener('keydown', keydown);
		document.addEventListener('keyup', keyup);
		return () => {
			canvas.removeEventListener('pointerdown', down);
			canvas.removeEventListener('pointermove', move);
			canvas.removeEventListener('pointerup', up);
			canvas.removeEventListener('pointercancel', up);
			document.removeEventListener('keydown', keydown);
			document.removeEventListener('keyup', keyup);
			stopDrawing();
			canvas.style.cursor = '';
			map.boxZoom.enable();
		};
	}, [mapRef, inited, active, boxClassName]);
};

export default useSelectMode;
